import { useState } from 'react';
import { useLanguage, useT } from '../i18n/i18n';
import Comments from './Comments';
import Evidence from './Evidence';
import { TARGET_STATUS_LABEL, deleteTarget, type Target } from './api';
import ActivityEditor from './ActivityEditor';
import ActivityItem from './ActivityItem';
import type { AskConfirm } from './GoalCard';
import { daysUntil, formatDay } from './GoalDates';
import TargetEditor from './TargetEditor';

export default function TargetCard({ learnerId, target, onChanged, ask }: {
  learnerId: string; target: Target; onChanged: () => void; ask: AskConfirm;
}) {
  const t = useT();
  const { lang } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [comments, setComments] = useState(false);
  const [evidence, setEvidence] = useState(false);
  const x = target;

  if (editing) {
    return (
      <li className="planner-target is-editing">
        <TargetEditor
          learnerId={learnerId}
          goalId={x.goalId}
          target={x}
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }}
          onDelete={() => ask({
            title: t('Delete this target?'),
            message: t('The target and its activities will be removed. This cannot be undone.'),
            confirmLabel: t('Delete target'),
            run: () => deleteTarget(learnerId, x.id),
          })}
        />
      </li>
    );
  }

  const days = x.reviewDate ? daysUntil(x.reviewDate) : null;
  const review = x.reviewDate == null || days == null ? null
    : days < 0 ? t('Review was due {date}', { date: formatDay(x.reviewDate, lang) })
      : days === 0 ? t('Review is due today')
        : days === 1 ? t('Review tomorrow')
          : t('Review in {n} days', { n: days });
  const provision = x.provisionWhat || x.provisionWho || x.provisionHowOften || x.provisionMinutes != null;

  return (
    <li className="planner-target">
      <div className="planner-target-head">
        <h4 className="planner-target-statement">{x.statement}</h4>
        <span className={`pill planner-goal-status is-${x.status.toLowerCase()}`}>{t(TARGET_STATUS_LABEL[x.status])}</span>
      </div>
      <dl className="planner-goal-meta">
        {x.successCriterion && <div><dt>{t('How we will know')}</dt><dd>{x.successCriterion}</dd></div>}
        {x.baseline && <div><dt>{t('What happens now')}</dt><dd>{x.baseline}</dd></div>}
        {x.byDate && <div><dt>{t('By when')}</dt><dd>{formatDay(x.byDate, lang)}</dd></div>}
        {x.reviewDate && (
          <div>
            <dt>{t('Review date')}</dt>
            <dd>
              {formatDay(x.reviewDate, lang)}
              <span className={`planner-target-review${days != null && days < 0 ? ' is-overdue' : ''}`}>{review}</span>
            </dd>
          </div>
        )}
      </dl>
      {provision && (
        <div className="planner-target-provision-view">
          <h5>{t('Provision')}</h5>
          <dl className="planner-goal-meta">
            {x.provisionWhat && <div><dt>{t('What')}</dt><dd>{x.provisionWhat}</dd></div>}
            {x.provisionWho && <div><dt>{t('Who runs it')}</dt><dd>{x.provisionWho}</dd></div>}
            {x.provisionHowOften && <div><dt>{t('How often')}</dt><dd>{x.provisionHowOften}</dd></div>}
            {x.provisionMinutes != null && <div><dt>{t('How long')}</dt><dd>{t('{n} minutes per session', { n: x.provisionMinutes })}</dd></div>}
          </dl>
        </div>
      )}
      <div className="planner-target-progress">
        <p>{x.progress.planned > 0 ? t('{done} of {planned} sessions done', { done: x.progress.done, planned: x.progress.planned }) : t('No sessions planned yet.')}</p>
        {x.progress.lastResult && <p>{t('Last result: {result}', { result: x.progress.lastResult })}</p>}
        {x.openComments > 0 && (
          <p className="planner-goal-waiting">
            {x.openComments === 1 ? t('1 question waiting') : t('{n} questions waiting', { n: x.openComments })}
          </p>
        )}
      </div>
      <div className="toolbar">
        <button type="button" className="btn secondary" onClick={() => setEditing(true)}>{t('Edit')}</button>
        <button type="button" className="btn secondary" onClick={() => setAdding(true)} disabled={adding}>{t('Add an activity')}</button>
        <button type="button" className="btn ghost" aria-expanded={comments} onClick={() => setComments(!comments)}>
          {comments ? t('Hide comments') : t('Comments')}
        </button>
        <button type="button" className="btn ghost" aria-expanded={evidence} onClick={() => setEvidence(!evidence)}>
          {evidence ? t('Hide evidence') : t('Evidence')}
        </button>
      </div>
      {comments && <Comments learnerId={learnerId} subjectType="TARGET" subjectId={x.id} onChange={onChanged} />}
      {evidence && <Evidence learnerId={learnerId} subjectType="TARGET" subjectId={x.id} />}
      <div className="planner-target-activities">
        <h5>{t('Activities')}</h5>
        {x.activities.length === 0 && !adding && <p className="muted">{t('No activities yet. An activity is how this target is taught.')}</p>}
        {x.activities.length > 0 && (
          <ul className="planner-activity-list">
            {x.activities.map((a) => <ActivityItem key={a.id} learnerId={learnerId} activity={a} onChanged={onChanged} ask={ask} />)}
          </ul>
        )}
        {adding && (
          <div className="planner-activity is-editing">
            <ActivityEditor learnerId={learnerId} targetId={x.id} activity={null} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); onChanged(); }} />
          </div>
        )}
      </div>
    </li>
  );
}
